import { useState } from 'react'
import { Link } from 'react-router-dom'
import { where } from 'firebase/firestore'
import { useAuth } from '../../lib/auth'
import { useCollection } from '../../lib/firestore'
import { useAirports } from '../../lib/airports'
import { countdown, dayKey, formatDayLong, formatTime, isOnDay } from '../../lib/format'
import type { Flight, Hotel, ItineraryItem, Show, Tour, TourCrew, WithId } from '../../types/models'
import Icon, { itineraryIcon } from '../../components/Icon'
import FlightCard from '../../components/FlightCard'
import ItineraryCard from '../../components/ItineraryCard'
import { HotelCard } from '../../components/Cards'
import SplitPane from '../../components/SplitPane'
import Empty from '../../components/Empty'
import DeleteConfirm from '../../components/DeleteConfirm'
import { CrewMemberCard } from './CrewTab'
import {
  deleteFlight,
  deleteHotel,
  deleteItineraryItem,
  removeCrewMember,
} from '../../lib/mutations'
import ItineraryDialog from '../../dialogs/ItineraryDialog'
import CrewDialog from '../../dialogs/CrewDialog'
import FlightDialog from '../../dialogs/FlightDialog'
import { HotelDialog } from '../../dialogs/TravelDialogs'

/**
 * The tour's own dashboard.
 *
 * Deliberately not the itinerary — that's what Schedule is for. This answers
 * the two questions someone opens a tour to ask on the day: what's next, and
 * where are we playing. Everything else is a summary with a way through to the
 * page that owns it.
 */
export default function OverviewTab({
  tour,
  canEdit,
}: {
  tour: WithId<Tour>
  canEdit: boolean
}) {
  const { user, uid } = useAuth()
  useAirports()

  const items = useCollection<ItineraryItem>('itineraryItems', [where('tourId', '==', tour.id)])
  const shows = useCollection<Show>('shows', [where('tourId', '==', tour.id)])
  const crew = useCollection<TourCrew>('tourCrew', [where('tourId', '==', tour.id)])
  const flights = useCollection<Flight>('flights', [where('tourId', '==', tour.id)])
  const hotels = useCollection<Hotel>('hotels', [where('tourId', '==', tour.id)])

  const [addingItem, setAddingItem] = useState(false)
  const [addingCrew, setAddingCrew] = useState(false)
  const [addingFlight, setAddingFlight] = useState(false)

  // One open card at a time, across every section. Expanding a flight while
  // an itinerary item is already open would push the whole rail around.
  const [openCard, setOpenCard] = useState<string | null>(null)
  const toggle = (id: string) => setOpenCard((current) => (current === id ? null : id))

  const [editingCrew, setEditingCrew] = useState<WithId<TourCrew> | null>(null)
  const [editingFlight, setEditingFlight] = useState<WithId<Flight> | null>(null)
  const [editingHotel, setEditingHotel] = useState<WithId<Hotel> | null>(null)
  const [deleting, setDeleting] = useState<{ label: string; run: () => Promise<void> } | null>(
    null,
  )

  const now = new Date()
  const startOfToday = new Date(now)
  startOfToday.setHours(0, 0, 0, 0)

  const sortedItems = [...items.data].sort(
    (a, b) => a.timeUTC.toMillis() - b.timeUTC.toMillis(),
  )
  // An item still waiting for a time is parked at midnight, so it isn't a
  // credible "next thing" — skip those rather than announcing 00:00.
  const nextUp = sortedItems.find(
    (item) => !item.needsTime && item.timeUTC.toDate() >= now,
  )
  const todayItems = sortedItems.filter((item) => isOnDay(item.timeUTC, now, item.timezone))

  const nextShow = [...shows.data]
    .sort((a, b) => a.date.toMillis() - b.date.toMillis())
    .find((show) => show.date.toDate() >= startOfToday)

  const upcomingFlights = [...flights.data]
    .filter((flight) => flight.arrivalTimeUTC.toDate() >= now)
    .sort((a, b) => a.departureTimeUTC.toMillis() - b.departureTimeUTC.toMillis())

  const currentHotel = [...hotels.data]
    .sort((a, b) => a.checkInDate.toMillis() - b.checkInDate.toMillis())
    .find((hotel) => hotel.checkOutDate.toDate() >= now)

  const nothingYet =
    !items.loading &&
    items.data.length === 0 &&
    shows.data.length === 0 &&
    crew.data.length === 0

  if (nothingYet) {
    return (
      <Empty
        title="This tour is empty"
        message="Add the shows first — each one seeds its own running order, so the day sheets exist before you know the times."
        action={canEdit ? { label: 'Add a show', to: `/tours/${tour.id}/shows` } : undefined}
      />
    )
  }

  return (
    <>
      <SplitPane
        storageKey="encore.overview.railWidth"
        defaultRailWidth={480}
        minRailWidth={300}
        primary={
          <div className="stack" style={{ gap: 24, paddingRight: 18 }}>
            {/* ------------------------------------------------- next up */}
            <section className="stack" style={{ gap: 10 }}>
              <div className="spread">
                <h2 style={{ fontSize: '1.05rem', fontWeight: 700 }}>Next up</h2>
                <Link className="btn btn-quiet" to={`/tours/${tour.id}/schedule`}>
                  Full schedule <Icon name="chevronRight" size={14} />
                </Link>
              </div>

              {!nextUp ? (
                <p className="panel panel-pad t-caption">
                  Nothing scheduled ahead.{' '}
                  {canEdit && (
                    <button className="accent" onClick={() => setAddingItem(true)}>
                      Add something
                    </button>
                  )}
                </p>
              ) : openCard === nextUp.id ? (
                // Opened, the hero becomes the same card every other itinerary
                // row uses — same detail, same edit affordances, nothing
                // special to keep in step.
                <ItineraryCard
                  item={nextUp}
                  tourId={tour.id}
                  userId={uid ?? ''}
                  open
                  onToggle={() => toggle(nextUp.id)}
                  canEdit={canEdit}
                  onDelete={() =>
                    setDeleting({
                      label: nextUp.title,
                      run: () => deleteItineraryItem(nextUp.id),
                    })
                  }
                />
              ) : (
                <button
                  type="button"
                  className="panel panel-signalled"
                  style={{ padding: 20, width: '100%', textAlign: 'left', cursor: 'pointer' }}
                  onClick={() => toggle(nextUp.id)}
                >
                  <div className="row" style={{ gap: 16, alignItems: 'flex-start' }}>
                    <span
                      style={{
                        display: 'grid',
                        placeItems: 'center',
                        width: 44,
                        height: 44,
                        flex: 'none',
                        borderRadius: 'var(--radius-control)',
                        background: 'var(--accent-wash)',
                        color: 'var(--accent-text)',
                      }}
                    >
                      <Icon name={itineraryIcon(nextUp.type)} size={21} />
                    </span>

                    <div className="grow stack" style={{ gap: 3, minWidth: 0 }}>
                      <span className="t-display-s truncate">{nextUp.title}</span>
                      {nextUp.subtitle && (
                        <span className="t-caption truncate">{nextUp.subtitle}</span>
                      )}
                      <span className="t-spec-caption faint">
                        {formatDayLong(nextUp.timeUTC, nextUp.timezone)}
                      </span>
                    </div>

                    <div className="stack" style={{ gap: 2, alignItems: 'flex-end' }}>
                      <span
                        className="t-timecode"
                        style={{ fontSize: '1.5rem', color: 'var(--accent-text)' }}
                      >
                        {formatTime(nextUp.timeUTC, nextUp.timezone)}
                      </span>
                      <span className="t-spec-caption faint">{countdown(nextUp.timeUTC)}</span>
                    </div>
                  </div>
                </button>
              )}
            </section>

            {/* --------------------------------------------------- today */}
            <section className="stack" style={{ gap: 10 }}>
              <div className="spread">
                <h2 style={{ fontSize: '1.05rem', fontWeight: 700 }}>Today</h2>
                <span className="t-caption">
                  {todayItems.length} item{todayItems.length === 1 ? '' : 's'}
                </span>
              </div>

              {todayItems.length === 0 ? (
                <p className="panel panel-pad t-caption">Nothing scheduled today.</p>
              ) : (
                <div className="stack" style={{ gap: 8 }}>
                  {todayItems.map((item) => (
                    <ItineraryCard
                      key={item.id}
                      item={item}
                      tourId={tour.id}
                      userId={uid ?? ''}
                      open={openCard === item.id}
                      onToggle={() => toggle(item.id)}
                      canEdit={canEdit}
                      onDelete={() =>
                        setDeleting({
                          label: item.title,
                          run: () => deleteItineraryItem(item.id),
                        })
                      }
                    />
                  ))}
                </div>
              )}
            </section>

            {/* ----------------------------------------------- next show */}
            <section className="stack" style={{ gap: 10 }}>
              <div className="spread">
                <h2 style={{ fontSize: '1.05rem', fontWeight: 700 }}>Next show</h2>
                <Link className="btn btn-quiet" to={`/tours/${tour.id}/shows`}>
                  All shows <Icon name="chevronRight" size={14} />
                </Link>
              </div>

              {nextShow ? (
                <Link
                  // The card names one show, so it opens that show. Landing on
                  // the list and having to find it again is the thing cards
                  // are supposed to save you.
                  to={`/tours/${tour.id}/shows/${nextShow.id}`}
                  className="panel"
                  style={{ display: 'block', padding: 20 }}
                >
                  <div className="spread" style={{ gap: 16, alignItems: 'flex-start' }}>
                    <div className="stack" style={{ gap: 4, minWidth: 0 }}>
                      <span className="t-display-m truncate">{nextShow.city}</span>
                      <span className="t-caption truncate">{nextShow.venueName}</span>
                      <span className="t-spec-caption faint">
                        {formatDayLong(nextShow.date, nextShow.timezone)}
                      </span>
                    </div>
                    <div className="stack" style={{ gap: 4, alignItems: 'flex-end' }}>
                      <span className="chip chip-accent">{countdown(nextShow.date)}</span>
                      {nextShow.headlinerSetTime && (
                        <span className="t-spec-caption faint">
                          Set {formatTime(nextShow.headlinerSetTime, nextShow.timezone)}
                        </span>
                      )}
                    </div>
                  </div>
                </Link>
              ) : (
                <p className="panel panel-pad t-caption">No upcoming shows on this tour.</p>
              )}
            </section>
          </div>
        }
        rail={
          <div className="stack" style={{ gap: 26, paddingLeft: 18 }}>
            {/* ---------------------------------------------------- crew */}
            <section className="stack" style={{ gap: 10 }}>
              <div className="spread">
                <h2 style={{ fontSize: '0.95rem', fontWeight: 700 }}>Crew</h2>
                <div className="row" style={{ gap: 4 }}>
                  <Link
                    className="btn btn-quiet"
                    to={`/tours/${tour.id}/crew`}
                    style={{ padding: '4px 8px' }}
                  >
                    All
                  </Link>
                  {canEdit && (
                    <button
                      onClick={() => setAddingCrew(true)}
                      aria-label="Add crew"
                      title="Add crew"
                      style={{ color: 'var(--text-secondary)' }}
                    >
                      <Icon name="plus" size={17} />
                    </button>
                  )}
                </div>
              </div>

              {crew.data.length === 0 ? (
                <p className="panel panel-pad t-caption">Nobody on the tour yet.</p>
              ) : (
                <div className="stack" style={{ gap: 8 }}>
                  {crew.data.slice(0, 6).map((member) => (
                    <CrewMemberCard
                      key={member.id}
                      member={member}
                      flights={flights.data}
                      hotels={hotels.data}
                      open={openCard === member.id}
                      onToggle={() => toggle(member.id)}
                      canManage={canEdit}
                      onEdit={() => setEditingCrew(member)}
                      onDelete={() =>
                        setDeleting({
                          label: member.name,
                          run: () => removeCrewMember(member),
                        })
                      }
                      inviter={{
                        id: uid ?? '',
                        name: user?.displayName || user?.email || 'Someone',
                      }}
                      tour={tour}
                    />
                  ))}
                  {crew.data.length > 6 && (
                    <Link
                      to={`/tours/${tour.id}/crew`}
                      className="t-caption accent"
                      style={{ display: 'block', padding: '11px 16px' }}
                    >
                      {crew.data.length - 6} more
                    </Link>
                  )}
                </div>
              )}
            </section>

            {/* ------------------------------------------------- flights */}
            <section className="stack" style={{ gap: 10 }}>
              <div className="spread">
                <h2 style={{ fontSize: '0.95rem', fontWeight: 700 }}>Upcoming flights</h2>
                <div className="row" style={{ gap: 4 }}>
                  <Link
                    className="btn btn-quiet"
                    to={`/tours/${tour.id}/travel`}
                    style={{ padding: '4px 8px' }}
                  >
                    All
                  </Link>
                  {canEdit && (
                    <button
                      onClick={() => setAddingFlight(true)}
                      aria-label="Add flight"
                      title="Add flight"
                      style={{ color: 'var(--text-secondary)' }}
                    >
                      <Icon name="plus" size={17} />
                    </button>
                  )}
                </div>
              </div>

              {upcomingFlights.length === 0 ? (
                <p className="panel panel-pad t-caption">No flights ahead.</p>
              ) : (
                <div className="stack" style={{ gap: 10 }}>
                  {upcomingFlights.slice(0, 3).map((flight) => (
                    <FlightCard
                      key={flight.id}
                      flight={flight}
                      crew={crew.data}
                      canEdit={canEdit}
                      onEdit={() => setEditingFlight(flight)}
                      onDelete={() =>
                        setDeleting({
                          label: `${flight.airline ?? ''} ${flight.flightNumber ?? ''}`.trim() ||
                            'this flight',
                          run: () => deleteFlight(flight.id),
                        })
                      }
                    />
                  ))}
                </div>
              )}
            </section>

            {/* -------------------------------------------------- hotel */}
            {currentHotel && (
              <section className="stack" style={{ gap: 10 }}>
                <h2 style={{ fontSize: '0.95rem', fontWeight: 700 }}>Where you're staying</h2>
                <HotelCard
                  hotel={currentHotel}
                  crew={crew.data}
                  canEdit={canEdit}
                  onEdit={() => setEditingHotel(currentHotel)}
                  onDelete={() =>
                    setDeleting({
                      label: currentHotel.name,
                      run: () => deleteHotel(currentHotel.id),
                    })
                  }
                />
              </section>
            )}
          </div>
        }
      />

      {addingItem && uid && (
        <ItineraryDialog
          open
          tourId={tour.id}
          userId={uid}
          defaultDate={dayKey(new Date())}
          onClose={() => setAddingItem(false)}
        />
      )}

      {addingCrew && uid && (
        <CrewDialog
          open
          tour={tour}
          inviter={{ id: uid, name: user?.displayName || user?.email || 'Someone' }}
          onClose={() => setAddingCrew(false)}
        />
      )}

      {addingFlight && uid && (
        <FlightDialog
          open
          tourId={tour.id}
          ownerId={uid}
          onClose={() => setAddingFlight(false)}
        />
      )}

      {editingCrew && uid && (
        <CrewDialog
          open
          member={editingCrew}
          tour={tour}
          inviter={{ id: uid, name: user?.displayName || user?.email || 'Someone' }}
          onClose={() => setEditingCrew(null)}
        />
      )}

      {editingFlight && uid && (
        <FlightDialog
          open
          flight={editingFlight}
          tourId={tour.id}
          ownerId={uid}
          onClose={() => setEditingFlight(null)}
        />
      )}

      {editingHotel && uid && (
        <HotelDialog
          open
          hotel={editingHotel}
          tourId={tour.id}
          ownerId={uid}
          onClose={() => setEditingHotel(null)}
        />
      )}

      {deleting && (
        <DeleteConfirm
          label={deleting.label}
          onCancel={() => setDeleting(null)}
          onConfirm={async () => {
            await deleting.run()
            setDeleting(null)
          }}
        />
      )}
    </>
  )
}

